import { v4 as uuidv4 } from 'uuid';
import { useRef, useState, useEffect } from 'react';

import { api } from 'api';
import { useAppDispatch } from 'store';
import { useAlert, handleMutation } from 'hooks';
import { cartApi, tagTypes } from 'store/api/cart';
import { CloverPayment, PaymentIntent, PaymentIntentStatus } from 'types';
import {
  getExternalPaymentId,
  saveExternalPaymentId,
  clearExternalPaymentIdIfMatches,
} from 'utils/storage';
import {
  useCancelPaymentMutation,
  useInitiatePaymentMutation,
} from 'store/api/clover';

import { PaymentStatus } from '../types';

import { emphasizeWarnings, recoveryGuidance, RecoveryNotice } from './recovery-guidance';

interface UseCreditPayment {
  tipAmount?: number;
  eventId: number;
  deviceId: number;
  checkoutId?: number;
  creditAmounts: number;
  processingFee: number;
  onSuccessPayment?: (payment: CloverPayment) => void;
  onSaleParked?: () => void;
}

const POLLING_INTERVAL = 5000;
const DELAYED_GUIDANCE_MS = 15_000;
const STATUS_CHECK_MS = 30_000;
const STATUS_READ_TIMEOUT_MS = 12_000;
// Time alone never closes a healthy wait (Owner 8 and 13 Oct: no timer exit).
// A lost terminal connection is handled by the server, which saves an approved
// card and parks the sale. Failed status reads keep retrying (Step 198).

export const useCreditPayment = ({
  tipAmount,
  eventId,
  deviceId,
  checkoutId,
  creditAmounts,
  processingFee,
  onSuccessPayment,
  onSaleParked,
}: UseCreditPayment) => {
  const dispatch = useAppDispatch();
  const { showErrorAlert } = useAlert();

  const requestActiveRef = useRef(false);
  const requestEpochRef = useRef(0);
  const requestReferenceRef = useRef<{ key: string; id: string } | null>(null);
  const animationFinishedRef = useRef(false);
  const shouldStopPollingRef = useRef(false);
  const isProcessingInCloverRef = useRef(false);
  const pendingStatusRef = useRef<PaymentStatus | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const [isCheckingExistingPayment, setCheckingExistingPayment] = useState(false);
  const [isTakingLonger, setTakingLonger] = useState(false);
  const [canCheckPaymentStatus, setCanCheckPaymentStatus] = useState(false);
  const [terminalUnreachable, setTerminalUnreachable] = useState(false);
  const autoContinueEpochRef = useRef(0);
  const continueRef = useRef<(() => unknown) | null>(null);
  const [isCheckingStatus, setCheckingStatus] = useState(false);
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const [recoveryNotice, setRecoveryNotice] = useState<RecoveryNotice | null>(
    null
  );
  const latestStatusRef = useRef<PaymentIntent | undefined>(undefined);
  const statusReadRef = useRef<{
    epoch: number;
    promise: Promise<boolean>;
  } | null>(null);
  const [isCancelDisabled, setCancelDisabled] = useState(true);
  const [signaturePending, setSignaturePending] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle');
  const [paymentIntentId, setPaymentIntentId] = useState<number | null>(null);
  const [paymentPendingAnimation, setPaymentPendingAnimation] = useState(false);

  const [cancelPayment, cancelPaymentMutation] = useCancelPaymentMutation();
  const [initiatePayment, initiatePaymentMutation] =
    useInitiatePaymentMutation();

  handleMutation(cancelPaymentMutation);
  handleMutation(initiatePaymentMutation);

  const completePaymentStatus = (status: PaymentStatus) => {
    if (animationFinishedRef.current) {
      setPaymentStatus(status);
    } else {
      pendingStatusRef.current = status;
    }
  };

  const stopPolling = () => {
    shouldStopPollingRef.current = true;
    requestActiveRef.current = false;

    if (pollingIntervalRef.current) {
      clearTimeout(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const clearCompletedReference = () => {
    const original = requestReferenceRef.current;
    if (original) clearExternalPaymentIdIfMatches(original.key, original.id);
  };

  const enterRecovery = (
    status?: Partial<PaymentIntent>,
    { alert = true }: { alert?: boolean } = {}
  ) => {
    const notice = recoveryGuidance(status);
    stopPolling();
    setCancelDisabled(true);
    setPaymentStatus('idle');
    setPaymentPendingAnimation(false);
    setRecoveryNotice(notice);
    setShowPaymentDialog(false);
    if (alert) showErrorAlert(notice.title, { description: notice.description });
  };

  const handlePaymentStatusResponse = (statusData: PaymentIntent): boolean => {
    const {
      status,
      canRetry,
      errorAction,
      errorMessage,
      cloverPayment,
      shouldResetExternalId,
    } = statusData;

    latestStatusRef.current = statusData;
    setTerminalUnreachable(statusData.terminalUnreachable === true);
    setApprovalConfirmed(statusData.approvalConfirmed === true);
    setSignaturePending(!!statusData.signaturePending);
    setCancelDisabled(statusData.canCancel !== true);
    isProcessingInCloverRef.current =
      !!statusData.isProcessingInClover || !!statusData.signaturePending;
    if (isProcessingInCloverRef.current) setCancelDisabled(true);

    // A terminal status probe can be inconclusive while the original PAY
    // worker is still active. Keep waiting; this is not a payment failure.
    const activeTerminalWait = status === PaymentIntentStatus.Pending &&
      statusData.isProcessingInClover === true && !errorMessage &&
      !statusData.recoveryAction && !statusData.shouldResetExternalId;
    if (activeTerminalWait && !statusData.approvalConfirmed && !statusData.signaturePending) {
      setCheckingExistingPayment(false);
    }

    // Owner 13 Oct: the terminal lost its connection after the card was
    // approved and saved; the server parked the sale for Receipts.
    if (statusData.parked === true) {
      stopPolling();
      // The next sale must never continue the parked sale's payment.
      clearCompletedReference();
      setRecoveryNotice(null);
      setPaymentStatus('idle');
      setPaymentPendingAnimation(false);
      setShowPaymentDialog(false);
      dispatch(cartApi.util.invalidateTags(tagTypes));
      onSaleParked?.();
      return true;
    }

    // One Credit press that only ended a stale terminal wait continues to the
    // signature instead of asking staff to press again (once per press).
    if (canRetry && !activeTerminalWait && statusData.approvalConfirmed && statusData.signaturePending &&
        statusData.recoveryStage === 'SIGNATURE_REQUIRED' && !statusData.recoveryAction &&
        autoContinueEpochRef.current !== requestEpochRef.current) {
      autoContinueEpochRef.current = requestEpochRef.current + 1;
      stopPolling();
      setTimeout(() => continueRef.current?.(), 0);
      return true;
    }

    if (
      (canRetry && !activeTerminalWait) ||
      (status === PaymentIntentStatus.Success &&
        (!cloverPayment || statusData.signaturePending))
    ) {
      enterRecovery(statusData);
      return true;
    }

    if (status === PaymentIntentStatus.Success && cloverPayment?.checkoutId &&
      cloverPayment.checkoutId !== checkoutId) {
      // A different tab may have finished this sale while our status read was
      // pending. Never apply its consumed payment to the current working cart.
      const notice: RecoveryNotice = {
        canContinue: false,
        title: 'Payment already recorded',
        description: `This payment is saved on receipt #${cloverPayment.checkoutId}. Open Receipts to view the completed sale. DO NOT PROCESS THIS PAYMENT AGAIN.`,
      };
      stopPolling();
      clearCompletedReference();
      setCancelDisabled(true);
      setPaymentStatus('idle');
      setPaymentPendingAnimation(false);
      setRecoveryNotice(notice);
      setShowPaymentDialog(false);
      dispatch(cartApi.util.invalidateTags(tagTypes));
      showErrorAlert(notice.title, { description: notice.description });
      return true;
    }

    if (status === PaymentIntentStatus.Success) {
      console.log('[PAYMENT] Payment successful');
      stopPolling();
      setRecoveryNotice(null);
      completePaymentStatus('success');
      setPaymentPendingAnimation(true);
      dispatch(cartApi.util.invalidateTags(tagTypes));

      // Clear only this request's hint before invoking caller callbacks. A
      // delayed response from another tab must not erase the next card's ID.
      clearCompletedReference();
      if (cloverPayment) {
        onSuccessPayment?.(cloverPayment);
      }
      return true;
    }

    if (
      status === PaymentIntentStatus.Failed ||
      status === PaymentIntentStatus.Timeout
    ) {
      if (
        !shouldResetExternalId ||
        cloverPayment ||
        statusData.cloverPaymentId ||
        statusData.approvalConfirmed ||
        statusData.signaturePending
      ) {
        enterRecovery(statusData);
        return true;
      }
      if (status === PaymentIntentStatus.Timeout) {
        // TIMEOUT alone is uncertain (handled above). The server explicitly
        // permits clearing only when no payment evidence remains. Its confirmed
        // cancellation response must not leave an idle dialog with no poll, or
        // imply that a customer's card was declined.
        stopPolling();
        clearCompletedReference();
        setRecoveryNotice(null);
        setPaymentIntentId(null);
        setShowPaymentDialog(false);
        setPaymentStatus('idle');
        setPaymentPendingAnimation(false);
        if (errorMessage) showErrorAlert(errorMessage, { description: emphasizeWarnings(errorAction) });
        return true;
      }
      setRecoveryNotice(null);
      console.log('[PAYMENT] Payment failed:', errorMessage);
      stopPolling();
      completePaymentStatus('failure');
      setPaymentPendingAnimation(true);

      // A failed response must not apply an unsigned or unverified payment.
      // Its original reference remains available for recovery.

      if (
        shouldResetExternalId &&
        !cloverPayment &&
        !statusData.signaturePending
      ) {
        clearCompletedReference();
      }

      if (errorMessage) {
        showErrorAlert(errorMessage, { description: emphasizeWarnings(errorAction) });
      }

      return true;
    }

    console.log('[PAYMENT] Payment still pending');
    return false;
  };

  const checkPaymentStatus = (
    intentId: number,
    epoch: number
  ): Promise<boolean> => {
    if (statusReadRef.current?.epoch === epoch)
      return statusReadRef.current.promise;
    const read = { epoch, promise: Promise.resolve(false) };
    statusReadRef.current = read;
    read.promise = (async () => {
      setCheckingStatus(true);
      const controller = new AbortController();
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const deadline = new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new Error('Payment status connection timed out'));
          }, STATUS_READ_TIMEOUT_MS);
        });
        const { data } = await Promise.race([
          api.payment.getPaymentStatus({ intentId, signal: controller.signal }),
          deadline,
        ]);
        if (epoch !== requestEpochRef.current || shouldStopPollingRef.current)
          return true;
        return handlePaymentStatusResponse(data);
      } catch {
        if (epoch !== requestEpochRef.current || shouldStopPollingRef.current)
          return true;
        // A failed status read says nothing about the terminal's payment.
        // Keep the original request alive and retry reads; never restart PAY.
        setTakingLonger(true);
        setCanCheckPaymentStatus(true);
        setCancelDisabled(true);
        return false;
      } finally {
        if (timer) clearTimeout(timer);
        if (statusReadRef.current === read) {
          statusReadRef.current = null;
          setCheckingStatus(false);
        }
      }
    })();
    return read.promise;
  };

  const onCheckPaymentStatus = () => {
    if (paymentIntentId && requestActiveRef.current) {
      return checkPaymentStatus(paymentIntentId, requestEpochRef.current);
    }
  };

  const startPolling = async (intentId: number, epoch: number) => {
    console.log('[POLLING] Starting polling for intentId:', intentId);
    shouldStopPollingRef.current = false;

    const poll = async () => {
      if (shouldStopPollingRef.current) {
        console.log('[POLLING] Polling cancelled by flag');
        return;
      }

      const shouldStop = await checkPaymentStatus(intentId, epoch);

      if (shouldStop) {
        console.log('[POLLING] Polling stopped');
        return;
      }

      if (shouldStopPollingRef.current) {
        console.log('[POLLING] Polling cancelled before scheduling next check');
        return;
      }

      console.log('[POLLING] Scheduling next check in', POLLING_INTERVAL, 'ms');

      pollingIntervalRef.current = setTimeout(() => {
        poll();
      }, POLLING_INTERVAL);
    };

    await poll();
  };

  const handleCreditPayment = async () => {
    if (requestActiveRef.current) return;
    if (!creditAmounts && !(tipAmount && tipAmount > 0)) {
      console.warn('[PAYMENT] No credit amounts provided');
      return;
    }

    const checkingExisting = !!getExternalPaymentId(String(eventId));
    setCheckingExistingPayment(checkingExisting);
    requestActiveRef.current = true;
    const epoch = ++requestEpochRef.current;
    setPaymentIntentId(null);
    latestStatusRef.current = undefined;
    setRecoveryNotice(null);
    setApprovalConfirmed(false);
    setSignaturePending(false);
    isProcessingInCloverRef.current = false;
    console.log('[PAYMENT] Starting credit payment');

    animationFinishedRef.current = checkingExisting;
    pendingStatusRef.current = null;
    shouldStopPollingRef.current = false;

    setCancelDisabled(true);
    setPaymentStatus('idle');
    setShowPaymentDialog(true);
    setPaymentPendingAnimation(false);

    try {
      let externalPaymentId = getExternalPaymentId(String(eventId));
      if (!externalPaymentId) {
        externalPaymentId = uuidv4().replace(/-/g, '').slice(0, 32);
        saveExternalPaymentId(String(eventId), externalPaymentId);
      }
      requestReferenceRef.current = { key: String(eventId), id: externalPaymentId };

      console.log(
        '[PAYMENT] Initiating payment with externalPaymentId:',
        externalPaymentId
      );

      const initiation = initiatePayment({
        eventId,
        deviceId,
        checkoutId,
        processingFee,
        externalPaymentId,
        amount: creditAmounts,
        ...(tipAmount !== undefined ? { tipAmount } : {}),
      });
      let initiationTimer: ReturnType<typeof setTimeout> | undefined;
      let initiateResponse: PaymentIntent;
      try {
        initiateResponse = await Promise.race([
          initiation.unwrap(),
          new Promise<never>((_, reject) => {
            initiationTimer = setTimeout(() => {
              initiation.abort?.();
              reject(new Error('Payment connection timed out'));
            }, STATUS_READ_TIMEOUT_MS);
          }),
        ]);
      } finally {
        if (initiationTimer) clearTimeout(initiationTimer);
      }

      if (epoch !== requestEpochRef.current) return;
      // The server may restore the cart's original reference after the browser
      // lost its copy. A late response must not overwrite a newer card's key.
      if (initiateResponse.externalPaymentId && initiateResponse.externalPaymentId !== externalPaymentId) {
        if (getExternalPaymentId(String(eventId)) === externalPaymentId) {
          saveExternalPaymentId(String(eventId), initiateResponse.externalPaymentId);
        }
        requestReferenceRef.current = { key: String(eventId), id: initiateResponse.externalPaymentId };
      }
      const {
        status,
        cloverPayment,
        paymentIntentId: intentId,
      } = initiateResponse;

      console.log('[PAYMENT] Initiate response - status:', status);

      setPaymentIntentId(intentId);

      const isComplete = handlePaymentStatusResponse(initiateResponse);

      if (isComplete) {
        return cloverPayment?.checkoutId && cloverPayment.checkoutId !== checkoutId
          ? undefined : cloverPayment;
      }

      if (status === PaymentIntentStatus.Pending) {
        console.log('[PAYMENT] Payment pending, starting polling');
        startPolling(intentId, epoch);
      }
    } catch (error: any) {
      if (epoch !== requestEpochRef.current) return;
      // A failed HTTP request cannot establish that Clover declined the card.
      enterRecovery(latestStatusRef.current);
    }
  };

  const onCancelPayment = async () => {
    if (isCancelDisabled || !paymentIntentId) return;
    const epoch = ++requestEpochRef.current;
    stopPolling();
    setShowPaymentDialog(false);
    if (!paymentIntentId) {
      showErrorAlert('Payment confirmation interrupted', {
        description:
          'Keep this cart open. DO NOT RESET THE CART. Click Credit again to check the original payment.',
      });
      return;
    }
    try {
      const result = await cancelPayment(paymentIntentId).unwrap();
      if (epoch !== requestEpochRef.current) return;
      if (
        result.status === PaymentIntentStatus.Success &&
        result.cloverPayment &&
        !result.signaturePending &&
        !result.canRetry
      ) {
        handlePaymentStatusResponse(result);
        return;
      }
      if (
        result.shouldResetExternalId &&
        !result.cloverPaymentId &&
        !result.cloverPayment &&
        !result.signaturePending
      ) {
        clearCompletedReference();
        setPaymentIntentId(null);
        return;
      }
    } catch (error) {
      // Rejected/uncertain cancellation is not permission to reset the terminal
      // or discard the original reference while an approval may be arriving.
      console.error('[PAYMENT] Cancellation not confirmed', error);
    }
    enterRecovery(latestStatusRef.current);
  };

  const onReturnToCheckout = () => {
    if (paymentStatus === 'idle' && !recoveryNotice) return;
    // Dismiss only the web waiting screen. Never cancel/reset Clover or discard
    // the original identity; the next Credit click resumes that same attempt.
    ++requestEpochRef.current;
    stopPolling();
    setShowPaymentDialog(false);
  };

  useEffect(() => {
    setTakingLonger(false);
    setCanCheckPaymentStatus(false);
    if (
      !showPaymentDialog ||
      paymentStatus !== 'idle' ||
      paymentPendingAnimation
    )
      return;
    const timer = setTimeout(() => setTakingLonger(true), DELAYED_GUIDANCE_MS);
    const returnTimer = setTimeout(
      () => setCanCheckPaymentStatus(true),
      STATUS_CHECK_MS
    );
    return () => {
      clearTimeout(timer);
      clearTimeout(returnTimer);
    };
  }, [showPaymentDialog, paymentStatus, paymentPendingAnimation]);

  const handleAnimationComplete = () => {
    animationFinishedRef.current = true;
    if (pendingStatusRef.current) {
      setPaymentStatus(pendingStatusRef.current);
      pendingStatusRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      requestEpochRef.current += 1;
      console.log('[PAYMENT] Component unmounting, cleaning up');
      stopPolling();
    };
  }, []);

  continueRef.current = handleCreditPayment;

  return {
    isCheckingExistingPayment,
    isTakingLonger,
    terminalUnreachable,
    canCheckPaymentStatus: canCheckPaymentStatus && !!paymentIntentId,
    onCheckPaymentStatus,
    isCheckingStatus,
    approvalConfirmed,
    isSavingSignature: approvalConfirmed && signaturePending && latestStatusRef.current?.recoveryStage === 'SIGNATURE_SAVE_PENDING',
    recoveryNotice,
    onReturnToCheckout,
    paymentStatus,
    signaturePending,
    paymentIntentId,
    onCancelPayment,
    isCancelDisabled,
    showPaymentDialog,
    handleCreditPayment,
    setShowPaymentDialog,
    handleAnimationComplete,
    paymentPendingAnimation,
  };
};
